import * as React from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';

import { Container } from '../../../layout/Container';

import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { BaseHeroContent, Hero } from '../../../ui/hero/Index';

import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  GeneralValuation,
  GeneralValuationVariables
} from '../../../../__types__/generated/GeneralValuation';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const FellesAmbisjon: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [lead, { error }] = useMutation<
    GeneralValuation,
    GeneralValuationVariables
  >(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.valuation?.success) {
        window.scroll(0, 0);
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.KONTOR,
              identifier: 'haugesund'
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="Felles ambisjon om høyest pris - PrivatMegleren"
        description="Prisen som oppnås på eiendom din er avgjørende, også for oss."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/content/ostbo/ostbo_1.jpeg"
        opacity={0.2}
        height="500px"
        className="top-header-margin"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            FELLES AMBISJON
            <br /> OM HØYEST PRIS
          </Heading>
          <Paragraph center>
            Prisen som oppnås på din eiendom er avgjørende, også for oss.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container
        className="padding-top"
        style={{ ...pageFadeIn, paddingTop: '80px', paddingBottom: '80px' }}
      >
        <Paragraph center>
          Derfor bruker vi mye tid på å avklare hvordan vi skal nå akkurat dine
          potensielle kjøpere, hvor de ferdes og hvordan de tenker. Vi skal ikke
          bare presentere en eiendom. Vi skal presentere et hjem andre har lyst
          på. Derfor er markedsføringen vi gjør avgjørende for å få flere
          interessenter. Vi vil ha flere kjøpere til nettopp din eiendom.
        </Paragraph>
        <Logo
          src="https://cdn.reeltime.no/pm_assets/content/ostbo/logo_ostbo3.png"
          alt="logo av logo"
        />
        <Paragraph center>
          Tlf. <a href="tel:52721100">52 72 11 00</a> <br />
          <a href="mailto:haugesund@privatmegleren.no">
            haugesund@privatmegleren.no
          </a>{' '}
          <br />
          Karmsundgaten 208 5527 Haugesund
        </Paragraph>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Paragraph center>
              Skal du selge eiendom? Fyll ut skjema og vi tar kontakt for en
              uforpliktende eiendomsprat.
            </Paragraph>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default FellesAmbisjon;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;

const Logo: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  return (
    <StyledLogoWrapper>
      <img src={src} alt={alt} />
    </StyledLogoWrapper>
  );
};

const StyledLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 70px 0 30px 0;

  img {
    width: 100%;
    max-width: 300px;
  }
`;
